import type { AnalyticsRange } from "api/billing";
import { addDaysToDate, daysBetween, formatDate } from "utils/format";

// Periods for the billing reports. Dates are IST "YYYY-MM-DD"; both ends are inclusive.

export type RangePresetId =
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "this_fy"
  | "last_12_months"
  | "custom";

/** The longest period the server accepts (3 years). */
export const MAX_RANGE_DAYS = 3 * 366;

/** First day of a month; `month` is 1–12 and may run past either end of the year. */
const firstOfMonth = (year: number, month: number): string =>
  new Date(Date.UTC(year, month - 1, 1)).toISOString().slice(0, 10);

const lastOfMonth = (year: number, month: number): string =>
  addDaysToDate(firstOfMonth(year, month + 1), -1);

const partsOf = (today: string) => ({
  year: Number(today.slice(0, 4)),
  month: Number(today.slice(5, 7)),
});

const PRESET_RANGES: Record<Exclude<RangePresetId, "custom">, (today: string) => AnalyticsRange> = {
  this_month: (today) => {
    const { year, month } = partsOf(today);
    return { from: firstOfMonth(year, month), to: lastOfMonth(year, month) };
  },
  last_month: (today) => {
    const { year, month } = partsOf(today);
    return { from: firstOfMonth(year, month - 1), to: lastOfMonth(year, month - 1) };
  },
  this_quarter: (today) => {
    const { year, month } = partsOf(today);
    const start = month - ((month - 1) % 3);
    return { from: firstOfMonth(year, start), to: lastOfMonth(year, start + 2) };
  },
  // India's financial year runs April to March.
  this_fy: (today) => {
    const { year, month } = partsOf(today);
    const startYear = month >= 4 ? year : year - 1;
    return { from: firstOfMonth(startYear, 4), to: lastOfMonth(startYear + 1, 3) };
  },
  last_12_months: (today) => {
    const { year, month } = partsOf(today);
    return { from: firstOfMonth(year, month - 11), to: lastOfMonth(year, month) };
  },
};

export const RANGE_PRESETS: Array<{ id: RangePresetId; label: string }> = [
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "this_quarter", label: "This quarter" },
  { id: "this_fy", label: "This financial year" },
  { id: "last_12_months", label: "Last 12 months" },
  { id: "custom", label: "Custom" },
];

export const presetRange = (id: Exclude<RangePresetId, "custom">, today: string): AnalyticsRange =>
  PRESET_RANGES[id](today);

/** Why a custom period can't be used, or null when it can. */
export const customRangeError = (range: AnalyticsRange): string | null => {
  if (!range.from || !range.to) {
    return "Choose both a start and an end date.";
  }
  const days = daysBetween(range.from, range.to);
  if (days === null) {
    return "Enter valid dates.";
  }
  if (days < 0) {
    return "The start date must be on or before the end date.";
  }
  if (days > MAX_RANGE_DAYS) {
    return "Choose a period of at most three years.";
  }
  return null;
};

/** "1 Oct 2026 – 31 Oct 2026", or one date when the period is a single day. */
export const formatRange = ({ from, to }: AnalyticsRange): string =>
  from === to ? formatDate(from) : `${formatDate(from)} – ${formatDate(to)}`;
